import { useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useApiService } from '@/hooks/useApiService';
import FormationColumns from './FormationColumns';
import closeIcon from '../images/close.svg';
import Cookies from 'js-cookie';
import { getTokenExpirationDate } from '../utils';
import { useUserContext } from '../contexts/userContext';
import { UserContext } from '../contexts/userContext';
import { getUserFromToken } from '../utils';
import { useTranslation } from 'react-i18next';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

type GettingStartedModalProps = {
    isOpen: boolean;
    setIsOpen: (value: boolean) => void;
};
export function GettingStartedModal({ isOpen, setIsOpen }: GettingStartedModalProps) {
    const [currentStep, setCurrentStep] = useState(1);
    const firstname = getUserFromToken()?.value.firstname || '';
    const lastname = getUserFromToken()?.value.lastname || '';

    const capitalizedfirstName = firstname.charAt(0).toUpperCase() + firstname.slice(1);
    const { t } = useTranslation();
    const decodeHtmlEntities = (text: string): string => {
        const textarea = document.createElement('textarea');
        textarea.innerHTML = text;
        return textarea.value;
    };

    function closeModal() {
        setIsOpen(false);
    }

    useEffect(() => {
        const currentDate = Date.now();
        const tokenDate = getTokenExpirationDate();
        const tokenExist: boolean = !!Cookies.get('token');
        if (tokenDate) {
            if (currentDate > tokenDate) {
                setCurrentStep(1);
            } else if (currentDate < tokenDate || !!tokenExist == false) {
                setCurrentStep(2);
            }
        }
    }, [setCurrentStep, isOpen]);

    return (
        <Transition as={Dialog} show={isOpen} className="relative z-50" onClose={closeModal}>
            <Transition.Child
                enter="ease-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
            >
                <div className="fixed inset-0 bg-white/40 backdrop-blur-3xl" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
                <div className="flex min-h-full items-center justify-center p-4">
                    <Transition.Child
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 scale-95"
                        enterTo="opacity-100 scale-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 scale-100"
                        leaveTo="opacity-0 scale-95"
                    >
                        <Dialog.Panel className=" w-fit  transform overflow-scroll rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all duration-500 overflow-y-auto border border-[#DFEAFF]">
                            {currentStep === 1 ? (
                                <LoginForm setCurrentStep={setCurrentStep} />
                            ) : (
                                <div className="relative max-w-[1200px] max-h-[80vh] flex flex-col items-center">
                                    <button
                                        className="absolute top-2 right-24 flex items-center rounded-full mb-6 justify-center w-12 h-12 bg-white/70 backdrop-blur-lg border border-[#EAECF0] shadow-sm hover:shadow-md hover:cursor-pointer active:shadow-none active:bg-gray-50"
                                        onClick={closeModal}
                                    >
                                        <img src={closeIcon} alt="Back arrow" />
                                    </button>
                                    <div className="max-w-[1200px] w-[90%] pt-16">
                                        <div className="flex flex-row items-center">
                                            <div className="flex h-11 w-11 items-center justify-center rounded-full border-2 border-white bg-[#DFEAFF] drop-shadow-none transition-all duration-500 ease-in-out cursor-pointer hover:shadow-lg active:shadow-sm">
                                                <span className="text-md font-semibold text-[#7FA1E2]">{`${
                                                    firstname?.charAt(0).toUpperCase() || ''
                                                }${lastname?.charAt(0).toUpperCase() || ''}`}</span>
                                            </div>
                                            <span className="font-semibold text-text-dark ml-2">
                                                {t('school_hello')} {decodeHtmlEntities(capitalizedfirstName)} !
                                            </span>
                                        </div>

                                        <h1 className="mt-8 font-semibold text-3xl text-text-dark">
                                            {t('school_pick_formation')}
                                        </h1>
                                        <div className="mt-8 w-full border-t border-[#E0E3E9]"></div>
                                        <FormationColumns isModalOpen={isOpen} setIsModalOpen={setIsOpen} />
                                    </div>
                                </div>
                            )}
                        </Dialog.Panel>
                    </Transition.Child>
                </div>
            </div>
        </Transition>
    );
}

export function LoginForm({ setCurrentStep }: { setCurrentStep: (value: number) => void }) {
    const { login, changeApiUrl } = useApiService();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const { t } = useTranslation();
    const { regions, login: userLogin } = useUserContext() as UserContext;

    async function onSubmit(event: { preventDefault: () => void }) {
        event.preventDefault();
        try {
            const credentials = await login(email, password);
            userLogin(credentials.token);

            setCurrentStep(2);
        } catch (error) {
            console.error(error);
            setErrorMessage('Email ou mot de passe incorrect');
        }
    }

    return (
        <div className="flex min-h-full m-auto flex-1 flex-col justify-center px-6 py-10 lg:px-6 w-[450px] max-w-[90%]">
            <div className="sm:mx-auto sm:w-full sm:max-w-sm">
                <img
                    className="w-[200px]"
                    src="https://www.archireport.com/wp-content/uploads/2019/10/logo-v6.png"
                    alt="Archireport"
                />
                <h2 className="mt-6 text-left text-2xl font-semibold leading-9 tracking-tight text-[#3f527e] max-w-[300px]">
                    {t('school_login_archireport')}
                </h2>
            </div>

            <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
                <form className="space-y-6" onSubmit={onSubmit}>
                    <div>
                        <label htmlFor="email" className="block text-sm font-medium leading-6 text-text-grey">
                            {t('school_email')}
                        </label>
                        <div className="mt-2">
                            <input
                                id="email"
                                name="email"
                                type="email"
                                autoComplete="email"
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                required
                                className="block w-full rounded-md border-0 py-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-ar-indigo sm:text-sm sm:leading-6"
                            />
                        </div>
                    </div>

                    <div>
                        <div className="flex items-center justify-between">
                            <label htmlFor="password" className="block text-sm font-medium leading-6 text-text-grey">
                                {t('school_password')}
                            </label>
                            <div className="text-sm">
                                <a href="#" tabIndex={-1} className="font-semibold text-ar-indigo">
                                    {t('school_forgot_password')}
                                </a>
                            </div>
                        </div>
                        <div className="mt-2">
                            <input
                                id="password"
                                name="password"
                                type="password"
                                autoComplete="current-password"
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                                required
                                className="block w-full rounded-md border-0 py-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-ar-indigo sm:text-sm sm:leading-6"
                            />
                        </div>
                    </div>
                    {errorMessage ? (
                        <div className="bg-ar-red/5 rounded-lg border border-ar-red/20 py-3 px-3">
                            <span className=" text-sm font-semibold text-ar-red ">{errorMessage}</span>
                        </div>
                    ) : null}

                    <div>
                        <button
                            type="submit"
                            className="flex w-full justify-center rounded-md bg-ar-indigo px-3 py-3 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                        >
                            {t('school_connect')}
                        </button>
                    </div>
                    <Select
                        defaultValue={regions.find(region => region.default)?.title}
                        onValueChange={value => {
                            console.log('value', value);
                            const foundRegion = regions.find(region => region.title === value);
                            console.log('foundRegion', foundRegion?.api);
                            if (foundRegion) {
                                changeApiUrl(foundRegion.api);
                            }
                        }}
                    >
                        <SelectTrigger className="w-full">
                            <SelectValue placeholder={t(regions.find(region => region.default)?.title || '')} />
                        </SelectTrigger>
                        <SelectContent>
                            {regions.map(region => (
                                <SelectItem key={region.title} value={region.title} className="w-full">
                                    {t(region.title)}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </form>

                <p className="mt-10 text-left text-sm text-gray-500">
                    {t('school_no_account')}
                    <br />
                    <a
                        href="https://web.archireport.com/subscribe"
                        className="font-semibold leading-6 text-ar-indigo hover:text-indigo-500"
                    >
                        {t('InApp_Conditions')}
                    </a>
                </p>
            </div>
        </div>
    );
}
